/**
 * Flag — robust country flag component using flagcdn.com SVG/PNG.
 * Falls back to emoji computed from the ISO code if the CDN image fails.
 *
 * Props:
 *  - code   : ISO 3166-1 alpha-2 (lowercase) — ex: "fr", "es"
 *  - emoji  : optional fallback emoji (🇫🇷)
 *  - size   : pixel size for the small flag (default 20)
 *  - className
 */
import React, { useState } from "react";

/** Convertit un code ISO alpha-2 en emoji drapeau (🇫🇷, 🇧🇷, etc.).
 *  Utilise les Regional Indicator Symbols Unicode — sans dépendance externe. */
function codeToEmoji(code) {
  const cc = (code || "").toUpperCase().slice(0, 2);
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  try {
    return String.fromCodePoint(
      ...cc.split("").map((ch) => 127397 + ch.charCodeAt(0)),
    );
  } catch {
    return "";
  }
}

export default function Flag({ code, emoji, size = 20, className = "", title = "", label = "" }) {
  const [failed, setFailed] = useState(false);
  const cc = (code || "").toLowerCase().slice(0, 2);
  const aspect = 4 / 3; // flagcdn ratio
  const w = Math.round(size * aspect);
  const h = size;

  // Emoji effectif : prop emoji > calculé depuis le code
  const effectiveEmoji = emoji || codeToEmoji(cc);

  if (!cc && effectiveEmoji) {
    return <span className={className} style={{ fontSize: size, lineHeight: 1 }} aria-label={label || title}>{effectiveEmoji}</span>;
  }
  if (!cc) return null;

  // Si l'image flagcdn n'a pas pu se charger → emoji (toujours disponible)
  if (failed) {
    return <span className={className} style={{ fontSize: size, lineHeight: 1 }} aria-label={label || title || cc.toUpperCase()}>{effectiveEmoji || cc.toUpperCase()}</span>;
  }

  // flagcdn.com supporte uniquement des largeurs standard : 20, 40, 80, 160, 320, 640, 1280, 2560.
  // On snap à la plus proche supérieure ou égale au besoin pour garantir le 200 OK.
  const STD_WIDTHS = [20, 40, 80, 160, 320, 640, 1280, 2560];
  const pickWidth = (need) => STD_WIDTHS.find((sw) => sw >= need) || STD_WIDTHS[STD_WIDTHS.length - 1];
  const w1x = pickWidth(w * 2);
  const w15x = pickWidth(w * 3);
  const w2x = pickWidth(w * 4);

  return (
    <img
      src={`https://flagcdn.com/w${w1x}/${cc}.png`}
      srcSet={`https://flagcdn.com/w${w15x}/${cc}.png 1.5x, https://flagcdn.com/w${w2x}/${cc}.png 2x`}
      width={w}
      height={h}
      alt={label || cc.toUpperCase()}
      title={title || label || cc.toUpperCase()}
      loading="lazy"
      className={`inline-block rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.35)] ${className}`}
      style={{ objectFit: "cover" }}
      onError={() => setFailed(true)}
    />
  );
}
